import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRegEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import { Loader, Pagination } from 'components';
import { routes } from 'common';
import { api } from 'api';
import { formatPrice } from 'common/utils';
import '../bookings/_bookings.scss';

const filterOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Declined', value: 'declined' },
];

const sortOptions = [
  { value: '-created', label: 'Date Added (Newest)' },
  { value: 'created', label: 'Date Added (Oldest)' },
  { value: '-modified', label: 'Date Modified (Newest)' },
  { value: 'modified', label: 'Date Modified (Oldest)' },
  { value: 'date', label: 'Booking Date (Earliest)' },
  { value: '-date', label: 'Booking Date (Latest)' },
  { value: 'price', label: 'Price (Lowest)' },
  { value: '-price', label: 'Price (Highest)' },
];

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

// Helper function to format time
const formatTime = (timeString) => {
  if (!timeString) return '';
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function getStatusBadgeClass(status) {
  switch (status) {
    case 'confirmed':
      return 'badge-success';
    case 'declined':
      return 'badge-danger';
    case 'cancelled':
      return 'badge-warning';
    case 'pending':
      return 'badge-secondary';
    default:
      return 'badge-secondary';
  }
}

function OfflineBooking() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch offline bookings
  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        page_size: itemsPerPage,
        ...(filter !== 'all' && { status: filter }),
        ...(search && { search }),
        ...(sortBy && { ordering: sortBy }),
      };

      const response = await api.getOfflineBookings(params);
      setBookings(response.results);
      setTotalItems(response.count);
    } catch (err) {
      setError(err);
      toast.error('Failed to fetch offline bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [currentPage, itemsPerPage, filter, search, sortBy]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (error) {
    return (
      <div className="bookings-page custom-listing">
        <div className="error-message">
          <h3>Error loading offline bookings</h3>
          <p>{error?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={fetchBookings}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-page custom-listing">
      {loading && <Loader />}
      <div className="listing-header">
        <h2 className="listing-title">Offline Bookings</h2>
        <button className="listing-create-btn" onClick={() => navigate(routes.OFFLINE_BOOKING_CREATE)}>Add Offline Booking</button>
      </div>
      <div className="listing-filters">
        <div className="filter-group">
          <Filters filters={filterOptions} selectedFilter={filter} handleFilter={setFilter} />
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={setSortBy} />
          <SearchBar value={search} onChange={setSearch} placeholder="Search player, ground..." debounceDelay={500} onDebouncedChange={setSearch} />
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Player</th>
              <th>Ground</th>
              <th>Field</th>
              <th>Date</th>
              <th>Slots</th>
              <th>Price (PKR)</th>
              <th>Status</th>
              <th>Date Created</th>
              <th>Date Modified</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', color: '#888' }}>No offline bookings found.</td>
              </tr>
            ) : (
              bookings.map(booking => (
                <tr key={booking.id}>
                  <td>{booking.id}</td>
                  <td>{booking.user?.full_name || booking.user?.email || 'N/A'}</td>
                  <td>{booking.ground_field?.ground?.name || 'N/A'}</td>
                  <td>{booking.ground_field?.name || 'N/A'}</td>
                  <td>{formatDate(booking.date)}</td>
                  <td>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</td>
                  <td>{formatPrice(booking.price || 0)}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(booking.status)}`}>{booking.status}</span>
                  </td>
                  <td>{formatDate(booking.created)}</td>
                  <td>{formatDate(booking.modified)}</td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => navigate(routes.BOOKING_DETAIL.replace(':id', booking.id))}
                      title="View booking details"
                    >
                      <FaRegEye />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />
    </div>
  );
}

export default OfflineBooking;